/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument */
// @ts-nocheck

// a series, in the pattern of d3fc-series, where each pair of points are
// drawn as distinct lines.

import { scaleIdentity } from 'd3-scale';
import { dataJoin, isTransition, rebind, rebindAll, exclude } from 'd3fc';
import { createBase, defined, functor } from './util';
import { select } from 'd3-selection';
import { line as lineShape } from 'd3-shape';

const LinesBase = () => {
  let baseValue = () => 0;
  const bandwidth = () => 5;
  let x0Value = (d) => d.x0;
  let y0Value = (d) => d.y0;
  let x1Value = (d) => d.x1;
  let y1Value = (d) => d.y1;
  let decorateEach = (_selection, _data, _index, _group) => {};

  const base = createBase({
    decorate: () => {},
    defined: (d, i) =>
      defined(baseValue, x0Value, y0Value, x1Value, y1Value)(d, i),
    xScale: scaleIdentity(),
    yScale: scaleIdentity(),
  });

  base.values = (d, i) => {
    const width = bandwidth(d, i);
    const xScale = base.xScale();
    const yScale = base.yScale();

    const x0 = xScale(x0Value(d, i), i);
    const y0 = yScale(y0Value(d, i), i);

    const x1 = xScale(x1Value(d, i), i);
    const y1 = yScale(y1Value(d, i), i);

    const yOffset = yScale(baseValue(d, i), i);

    return {
      d,
      x0,
      y0,
      x1,
      y1,
      yOffset,
      width,
      height: Math.max(y0, y1) - yOffset,
      origin: [x0, y0],
      baseOrigin: [x0, yOffset],
    };
  };

  base.baseValue = (...args) => {
    if (!args.length) {
      return baseValue;
    }
    baseValue = functor(args[0]);
    return base;
  };

  base.x0Value = (...args) => {
    if (!args.length) {
      return x0Value;
    }
    x0Value = functor(args[0]);
    return base;
  };
  base.y0Value = (...args) => {
    if (!args.length) {
      return y0Value;
    }
    y0Value = functor(args[0]);
    return base;
  };

  base.x1Value = (...args) => {
    if (!args.length) {
      return x1Value;
    }
    x1Value = functor(args[0]);
    return base;
  };
  base.y1Value = (...args) => {
    if (!args.length) {
      return y1Value;
    }
    y1Value = functor(args[0]);
    return base;
  };

  base.decorateEach = (...args) => {
    if (!args.length) {
      return decorateEach;
    }
    decorateEach = functor(args[0]);
    return base;
  };

  return base;
};

export const SeriesSvgLines = () => {
  const base = LinesBase();

  const lineData = lineShape()
    .x((d, _i) => d[0])
    .y((d, _i) => d[1]);

  const join = dataJoin('g', 'lines');

  const propagateTransition = (maybeTransition) => (selection) =>
    (isTransition(maybeTransition) as boolean)
      ? selection.transition(maybeTransition)
      : selection;

  const lines = (selection) => {
    if (isTransition(selection) as boolean) {
      join.transition(selection);
    }

    const transitionPropagator = propagateTransition(selection);

    selection.each((data, index, group) => {
      const filteredData = data.filter(base.defined());

      const g = join(select(group[index]), filteredData);
      g.exit().remove();

      g.enter()
        .attr('data-series-kind', 'lines')
        .append('path')
        .attr('fill', 'none')
        .attr('stroke', '#000');

      g.each((d, i, g) => {
        const { x0, y0, x1, y1 } = base.values(d, i);

        const sel = transitionPropagator(select(g[i]));

        sel.select('path').attr(
          'd',
          lineData([
            [x0, y0],
            [x1, y1],
          ]),
        );

        base.decorateEach()(sel, d, i, g);
      });

      base.decorate()(g, data, index);
    });
  };

  rebindAll(lines, base, exclude('baseValue', 'bandwidth', 'align'));
  rebind(lines, join, 'key');
  rebind(lines, lineData, 'curve');

  return lines;
};
